/* CookbookTrash — 30-day grace list of soft-deleted cookbooks with
 * one-tap Restore and the "scary" Delete-forever action. Only reachable
 * when the Dashboard header shows the Trash link (ie trashCount > 0).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowCounterClockwise, Trash, ArrowLeft } from "@phosphor-icons/react";
import PageMeta from "@/components/PageMeta";
import BookCover from "@/components/BookCover";
import { RecipeTrash } from "@/components/RecipeTrash";

export default function CookbookTrash() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // When set, we show the final "Delete forever?" confirmation modal
  // for the matching cookbook_id. Hard-delete is irreversible so the
  // extra tap is deliberate.
  const [confirmPurge, setConfirmPurge] = useState(null);
  const [recipeCount, setRecipeCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cookbooks/trash");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Trash");
      setItems([]);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) nav("/login", { replace: true });
  }, [loading, user, nav]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const handleRestore = async (cb) => {
    setBusyId(cb.cookbook_id);
    try {
      await api.post(`/cookbooks/${cb.cookbook_id}/restore`);
      toast.success(`"${cb.title}" restored.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setBusyId(null);
    }
  };

  const handlePurge = async (cb) => {
    setBusyId(cb.cookbook_id);
    try {
      await api.delete(`/cookbooks/${cb.cookbook_id}/purge`);
      toast.success(`"${cb.title}" deleted forever.`);
      setConfirmPurge(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="trash-page">
      <PageMeta title="Trash — RecipeFiler" description="Restore cookbooks you've deleted in the last 30 days." noindex={true} />

      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-rf-ink-soft hover:text-rf-ink mb-3" data-testid="trash-back">
        <ArrowLeft size={16} /> Back to Dashboard
      </Link>

      <h1 className="font-display font-black text-4xl text-rf-ink mb-2">Trash</h1>
      <p className="text-rf-ink-soft mb-8 max-w-2xl">
        Cookbooks and recipes you&rsquo;ve moved to Trash stay here for <b>30 days</b>, then they&rsquo;re permanently
        deleted (a cookbook takes every recipe, photo, scan and audio memory inside with it). You can
        <b> Restore</b> anything in one tap before then.
      </p>

      {items === null && (
        <div className="text-rf-ink-soft text-sm" data-testid="trash-loading">Loading…</div>
      )}

      {items && items.length === 0 && recipeCount === 0 && (
        <div className="rounded-3xl bg-rf-cream-alt p-10 text-center" data-testid="trash-empty">
          <div className="text-5xl mb-3" aria-hidden>🧺</div>
          <h2 className="font-display font-black text-xl text-rf-ink mb-1">Trash is empty</h2>
          <p className="text-rf-ink-soft text-sm">
            Nothing waiting to be restored. Deleted cookbooks and recipes will appear here for 30 days.
          </p>
        </div>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-4" data-testid="trash-list">
          {items.map((cb) => (
            <li
              key={cb.cookbook_id}
              data-testid={`trash-row-${cb.cookbook_id}`}
              className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-2xl bg-white border border-rf-ink/10 shadow-sm"
            >
              <div className="w-20 shrink-0">
                <BookCover
                  title={cb.title}
                  subtitle={cb.subtitle}
                  coverImageUrl={cb.cover_image_url}
                  logoUrl={cb.logo_url}
                  showTitle={true}
                  className="w-full"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-black text-lg text-rf-ink truncate">{cb.title}</h3>
                <p className="text-xs text-rf-ink-soft">
                  {cb.recipe_count} {cb.recipe_count === 1 ? "recipe" : "recipes"} inside ·{" "}
                  <span className={cb.days_left <= 3 ? "text-red-700 font-bold" : ""}>
                    {cb.days_left === 0 ? "Purging today" : `${cb.days_left} day${cb.days_left === 1 ? "" : "s"} left`}
                  </span>
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  data-testid={`trash-restore-${cb.cookbook_id}`}
                  onClick={() => handleRestore(cb)}
                  disabled={busyId === cb.cookbook_id}
                  className="btn-amber !py-2 !px-3 text-sm"
                >
                  <ArrowCounterClockwise size={16} /> Restore
                </button>
                <button
                  type="button"
                  data-testid={`trash-purge-${cb.cookbook_id}`}
                  onClick={() => setConfirmPurge(cb)}
                  disabled={busyId === cb.cookbook_id}
                  className="btn-outline-ink !py-2 !px-3 text-sm !text-red-700 !border-red-300 hover:!bg-red-50"
                >
                  <Trash size={16} /> Delete forever
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {user && <RecipeTrash onCount={setRecipeCount} />}

      {/* Hard-delete confirmation */}
      {confirmPurge && (
        <div
          className="fixed inset-0 z-[60] bg-rf-ink/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { if (busyId !== confirmPurge.cookbook_id) setConfirmPurge(null); }}
          data-testid="trash-purge-modal"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center">
                <Trash size={22} weight="duotone" className="text-red-700" aria-hidden="true" />
              </div>
              <h3 className="font-display font-black text-2xl text-rf-ink leading-tight">
                Delete forever?
              </h3>
            </div>
            <p className="text-sm text-rf-ink mb-5">
              This will permanently delete <b>&ldquo;{confirmPurge.title}&rdquo;</b> and its
              {" "}<b>{confirmPurge.recipe_count}</b> {confirmPurge.recipe_count === 1 ? "recipe" : "recipes"}
              {" "}— including every photo, scan and audio memory inside. <b>This can&rsquo;t be undone.</b>
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                data-testid="trash-purge-cancel"
                onClick={() => setConfirmPurge(null)}
                disabled={busyId === confirmPurge.cookbook_id}
                className="btn-outline-ink justify-center flex-1"
              >
                Keep in Trash
              </button>
              <button
                type="button"
                data-testid="trash-purge-confirm"
                onClick={() => handlePurge(confirmPurge)}
                disabled={busyId === confirmPurge.cookbook_id}
                className="justify-center flex-1 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busyId === confirmPurge.cookbook_id ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
